import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import {
    SiteStructure,
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
    FOOTER_LINKS_SHAPE_PROP_TYPE,
} from '@eb/site-structure';

import { Icon } from '@eb/eds-icon';
import { Button } from '@eb/eds-button';
import { AlertChunky as AlertChunkySvg } from '@eb/eds-iconography';
import { gettext } from '@eb/i18n';

// Deal with the messed up Varnish urls (/esi_cache/?...) which break the signin link
const fixLoginUrl = (loginUrl) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

const HEADER_TEXT = gettext('Oops, something went wrong!');
const SUBTITLE_TEXT = gettext(
    'If you feel that this message is in error, please <a href="/support/contact-us">let us know</a>.',
);
const BUTTON_CREATE_EVENT_CTA = gettext('Create an Event');
const BUTTON_FIND_EVENT_CTA = gettext('Find an Event');

const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    marginTop: '-40px',
};

const RootErrorFallback = () => {
    return (
        <div style={containerStyles}>
            <Icon type={<AlertChunkySvg />} size="large" color="ui-red" />
            <section className="eds-l-mar-vert-2 eds-text--center">
                <h1 className="eds-text-hl">{HEADER_TEXT}</h1>
                <div className="eds-l-pad-top-1 eds-text-color--gray-600 eds-text-bl">
                    <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: SUBTITLE_TEXT.toString(),
                        }}
                    />
                </div>
            </section>
            <section className="eds-l-pad-vert-1">
                <Button style="fill" type="link" href="/create">
                    {BUTTON_CREATE_EVENT_CTA}
                </Button>
                <span className="eds-l-pad-hor-2" />
                <Button style="neutral" type="link" href="/d/local/">
                    {BUTTON_FIND_EVENT_CTA}
                </Button>
            </section>
        </div>
    );
};

export default class BasePage extends React.Component {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        footerLinks: FOOTER_LINKS_SHAPE_PROP_TYPE.isRequired,
        isMobile: PropTypes.bool,
        handleSearch: PropTypes.func,
        shouldStopRenderEarly: PropTypes.bool,
        isHomePage: PropTypes.bool,
        isSearchPage: PropTypes.bool,
        children: PropTypes.node,
        structureHeader: PropTypes.func,
    };

    componentDidMount() {
        // Accessibility improvement on a component with (still) unknown location
        const userMenuItem = document.querySelector(
            '.consumer-header__user-menu.consumer-header__menu',
        );

        if (userMenuItem) {
            const userMenuLink = userMenuItem.querySelector('a');

            if (userMenuLink) {
                userMenuLink.setAttribute('aria-label', gettext('User menu'));
            }
        }
    }

    render() {
        const {
            env,
            request,
            user,
            footerLinks,
            isMobile,
            handleSearch,
            isHomePage,
            isSearchPage,
            featureFlags,
            seasonalContent,
            structureHeader,
            removeOverflowSettings,
        } = this.props;

        const Header = structureHeader;

        const _env = {
            ...env,
            loginUrl: fixLoginUrl(env.loginUrl),
            signinUrl: fixLoginUrl(env.signinUrl),
            signupUrl: fixLoginUrl(env.signupUrl),
        };

        return (
            <SiteStructure
                env={_env}
                request={request}
                user={user}
                featureFlags={featureFlags}
                removeOverflowSettings={removeOverflowSettings}
                fullScreenMain={true}
                globalHeader={
                    <Header
                        env={_env}
                        request={request}
                        user={user}
                        handleSearch={handleSearch}
                        isMobile={isMobile}
                        isHomePage={isHomePage}
                        isSearchPage={isSearchPage}
                        featureFlags={featureFlags}
                        seasonalContent={seasonalContent}
                    />
                }
                isUserAuthenticated={user.isAuthenticated}
                showMarketingLinks={!user.isAuthenticated}
                footerLinks={footerLinks}
            >
                {/* Sitestructure has an error boundary built in around its children, so errors that sentry
                should be grabbing are potentially being swallowed. This ensures that the error is caught
                and not piped to bugsnag. */}
                <Sentry.ErrorBoundary
                    fallback={<RootErrorFallback />}
                    onError={(error) => {
                        if (process.env.NODE_ENV !== 'production') {
                            throw error;
                        }
                    }}
                >
                    {this.props.children}
                </Sentry.ErrorBoundary>
            </SiteStructure>
        );
    }
}
